import { GAME_OPPOSITE_OPTIONS } from 'betgames-contract-js';
import { GameId } from '@bg-shared/enums/GameId';
import { t } from '@bg-shared/business/Translate';
import { partnerSettingsStore } from '@bg-shared/business/PartnerSettings';
import { CasinoValidationService } from '@bg-shared/business/CasinoBetting/service/CasinoValidation.service';
import {
    ICasinoValidationParams,
    ICasinoValidationError,
    ICasinoRecentActionsModel,
} from '@bg-shared/business/CasinoBetting/interfaces';
import { keyBy } from 'lodash-unified';

export class WheelValidationService extends CasinoValidationService {
    private colorOptionsMap: Record<string, string[]> = {};

    private optionClassToIdMap: Record<string, string> = {};

    constructor(private readonly gameId: GameId) {
        super(gameId);

        if (partnerSettingsStore.isOppositeBettingRestricted) {
            this.buildOppositeOptionsMap(gameId);

            this.defaultAddValidators.push(this.validateOppositeBets);
            this.defaultMoveValidators.push(this.validateOppositeBets);
        }
    }

    private buildOppositeOptionsMap(gameId: GameId) {
        if (!this.gameOptionsStore.value) {
            return;
        }

        const oppositeOptions = GAME_OPPOSITE_OPTIONS[gameId] as Record<string, string[]>;
        const groupedOptions = keyBy(this.gameOptionsStore.value.options, 'optionClass');

        Object.entries(groupedOptions).forEach(([optionClass, option]) => {
            this.optionClassToIdMap[optionClass] = option.id.toString();
        });

        Object.entries(oppositeOptions).forEach(([optionClass, oppositeClasses]) => {
            if (this.optionClassToIdMap[optionClass]) {
                this.colorOptionsMap[this.optionClassToIdMap[optionClass]] = oppositeClasses
                    .map((cls) => this.optionClassToIdMap[cls])
                    .filter(Boolean);
            }
        });
    }

    private validateOppositeBets(params: ICasinoValidationParams): ICasinoValidationError {
        // Check if this option has opposite bets
        const conflictOptionIds = this.colorOptionsMap[params.optionItemID];

        if (!conflictOptionIds) {
            return null;
        }

        // Check if placing this bet would exceed two opposite bets
        const existingConflictBets = conflictOptionIds.filter(
            (id) => this.betAmountsStore.value[id] > 0,
        );

        // If we're moving a bet from one of the opposing colors, we can allow this bet
        const isMovingFromConflict =
            params.moveFromOptionItemID && conflictOptionIds.includes(params.moveFromOptionItemID);

        if (existingConflictBets.length >= 2 && !isMovingFromConflict) {
            return {
                ...this.defaultValidationError,
                message: t.string('cannot_place_bets_on_all_outcomes'),
            };
        }

        return null;
    }

    private validateOppositeBetsRepeat(bets: ICasinoRecentActionsModel['recentBets']): boolean {
        if (!bets.length) {
            return true;
        }

        const oppositeOptions = GAME_OPPOSITE_OPTIONS[this.gameId] as Record<string, string[]>;

        if (!oppositeOptions) {
            return true;
        }

        const betOptionClasses = bets.map((bet) => bet.oddClass);
        const hasAllThreeOppositeOptions = Object.entries(oppositeOptions).some(
            ([baseOption, opposites]) =>
                betOptionClasses.includes(baseOption) &&
                opposites.every((option) => betOptionClasses.includes(option)),
        );

        if (hasAllThreeOppositeOptions) {
            super.showError(t.string('cannot_place_bets_on_all_outcomes'));
            return false;
        }

        return true;
    }

    public validateRepeat(bets: ICasinoRecentActionsModel['recentBets']): boolean {
        const isValid = super.validateRepeat(bets);

        if (!isValid) {
            return false;
        }

        if (partnerSettingsStore.isOppositeBettingRestricted) {
            return this.validateOppositeBetsRepeat(bets);
        }

        return true;
    }
}
